/**
 * Authentication context provider
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../services/firebase';
import { authApi } from '../services/api';
import { User } from '../types';
import { initializeUserDataSync, clearUserDataOnLogout } from '../services/userDataSync';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch user data with permissions from API
   */
  async function fetchUserData(): Promise<void> {
    try {
      const data = await authApi.me();
      setUserData(data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUserData(null);
    }
  }

  /**
   * Sign in with email and password
   */
  async function signIn(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(auth, email, password);
    // User data will be fetched in onAuthStateChanged
  }

  /**
   * Sign up new user
   */
  async function signUp(
    email: string,
    password: string,
    displayName?: string
  ): Promise<void> {
    // Create user via API (handles role assignment)
    await authApi.register({ email, password, displayName });

    // Sign in the newly created user
    await signInWithEmailAndPassword(auth, email, password);
  }

  /**
   * Sign out
   */
  async function signOut(): Promise<void> {
    // Clear IndexedDB user data
    await clearUserDataOnLogout();

    await firebaseSignOut(auth);
    setUserData(null);
  }

  /**
   * Listen to auth state changes
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        await fetchUserData();

        // Initialize user data sync - fetches and caches user bundle
        await initializeUserDataSync();
      } else {
        setUserData(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    userData,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
